import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFontes } from '../../hooks/useFontes';
import { FonteInput } from '../../types/fonte';
import { Input } from '../../components/Input';
import { Button } from '../../components/Button';
import { Select } from '../../components/Select';
import { useMontadoras } from '../../hooks/useMontadoras';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-toastify';

const FonteId: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { useFonte, criarFonte, atualizarFonte, listLoading } = useFontes();
  const { data: fonte, isLoading: fonteLoading } = useFonte(id && id !== 'novo' ? id : '');
  const { montadoras } = useMontadoras();
  
  const [formData, setFormData] = useState<FonteInput>({  
    nome: '',
    id_montadora: '',
    nome_montadora: '',
    arquivo: '',
    demonstracao: '',
    tamanho: 0,
    espacamento: 0
  });

  useEffect(() => {
    if (fonte) {
      setFormData({
        nome: fonte.nome,
        id_montadora: fonte.id_montadora,
        nome_montadora: fonte.nome_montadora,
        arquivo: fonte.arquivo,
        demonstracao: fonte.demonstracao,
        tamanho: fonte.tamanho,
        espacamento: fonte.espacamento
      });
    }
  }, [fonte]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // O campo arquivo é readOnly (preenchido apenas pelo upload), então não pode
    // usar "required" do HTML. Sem esta validação, um upload que falhou grava a
    // fonte sem arquivo e quebra a atualização de fontes de todos os usuários.
    if (!formData.arquivo?.trim()) {
      toast.error('Selecione o arquivo .ttf da fonte antes de salvar');
      return;
    }

    if (!id || id === 'novo') {
      await criarFonte(formData);
    } else {
      await atualizarFonte({ id, fonte: formData });
    }

    navigate('/fontes');
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (file && file.name.toLowerCase().endsWith('.ttf')) {
      try {
        // Upload do arquivo para o Supabase Storage
        const fileName = file.name; // Apenas o nome do arquivo
        const { error } = await supabase.storage
          .from('assets')
          .upload(`fonts/${fileName}`, file, {
            cacheControl: '3600',
            upsert: true
          });

        if (error) throw error;

        // Atualizar formData com o nome do arquivo
        setFormData(prev => ({ ...prev, arquivo: fileName }));
        
        toast.success('Arquivo salvo com sucesso');
      } catch (err) {
        console.error('Erro detalhado:', err);
        toast.error('Erro ao salvar arquivo');
      }
    } else {
      toast.error('Por favor, selecione um arquivo .ttf');
    }
  };

  if (id !== 'novo' && fonteLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">
        {!id || id === 'novo' ? 'Nova Fonte' : 'Editar Fonte'}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Nome</label>
          <Input
            type="text"
            value={formData.nome}
            onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
            required
          />
        </div>

        <div>
          <Select
            label="Montadora"
            value={formData.id_montadora}
            onChange={(e) => {
              const montadora = montadoras?.find(m => m.id === e.target.value);
              setFormData({ 
                ...formData, 
                id_montadora: e.target.value,
                nome_montadora: montadora?.nome || ''
              });
            }}
            options={montadoras?.map(montadora => ({
              value: montadora.id,
              label: montadora.nome
            })) || []}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Arquivo da Fonte</label>
          <div className="flex items-center gap-2">
            <Input
              type="text"
              value={formData.arquivo || ''}
              readOnly
              placeholder="Nenhum arquivo selecionado"
              className="mr-2"
            />
            <input
              type="file"
              id="file-upload"
              className="hidden"
              onChange={handleFileSelect}
              accept=".ttf"
            />
            <Button
              type="button"
              onClick={() => document.getElementById('file-upload')?.click()}
              className="bg-blue-500 hover:bg-blue-600"
            >
              Escolher
            </Button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Texto de Demonstração</label>
          <Input
            type="text"
            value={formData.demonstracao}
            onChange={(e) => setFormData({ ...formData, demonstracao: e.target.value })}
            placeholder="Digite um texto para demonstrar a fonte"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Tamanho</label>
            <Input
              type="number"
              value={formData.tamanho}
              onChange={(e) => setFormData({ ...formData, tamanho: Number(e.target.value) })}
              min={0}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Espaçamento</label>
            <Input
              type="number"
              value={formData.espacamento}
              onChange={(e) => setFormData({ ...formData, espacamento: Number(e.target.value) })}
              min={0}
              required
            />
          </div>
        </div>

        <div className="flex gap-4">          
          <Button
            type="button"
            onClick={() => navigate('/fontes')}
            className="bg-gray-500 hover:bg-gray-600"
          >
            Cancelar
          </Button>
          <Button type="submit" disabled={listLoading || fonteLoading}>
            Salvar
          </Button>
        </div>
      </form>
    </div>
  );
};

export default FonteId; 